import { times as _times } from 'lodash-es'
import { Factory, Model } from 'miragejs'
import { getQueryParamValue } from '@@/api/mock-server/utils.js'
import { randMask } from '@ngneat/falso'

import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@@/api/mock-server/server'
import type { ReportSummaryTimePeriod } from '@/components/reports/summary/config'
import type {
  SummaryConsultationData,
  SummaryConsultationDataBE,
} from '@/composables/api/ReportsSummary.js'
import { safeMathRandom } from '@/utils/number.js'
import { getCurrentYear } from '@/utils/date/getter.js'

export const model = Model as ModelDefinition<SummaryConsultationData>

const NR_OF_SERIES_ITEMS = 6

export const factory = Factory.extend<SummaryConsultationDataBE>({
  id() {
    return randMask({ mask: '################' })
  },
  current_total() {
    return {
      id: randMask({ mask: '################' }),
      period_label: 'current',
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }
  },
  previous_total() {
    return {
      id: randMask({ mask: '################' }),
      period_label: 'previous',
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }
  },
  totals_delta() {
    return parseFloat((safeMathRandom() * 40 - 20).toFixed(1))
  },
  current_series() {
    return _times(NR_OF_SERIES_ITEMS, (i) => ({
      id: randMask({ mask: '################' }),
      index_label: String(i),
      real_label: ` ${i + 1}`,
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }))
  },
  previous_series() {
    return _times(NR_OF_SERIES_ITEMS, (i) => ({
      id: randMask({ mask: '################' }),
      index_label: String(i),
      real_label: ` ${i + 1}`,
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }))
  },
})

export function getMockPeriodValues(): Record<
  string,
  { current: string[]; previous: string[] }
> {
  const currentYear = getCurrentYear()
  const previousYear = currentYear - 1
  const shortYear = currentYear.toString().slice(-2)

  return {
    day: {
      current: [
        `25 Mar ${shortYear}`,
        `26 Mar ${shortYear}`,
        `27 Mar ${shortYear}`,
        `28 Mar ${shortYear}`,
        `29 Mar ${shortYear}`,
        `30 Mar ${shortYear}`,
      ],
      previous: [
        `18 Mar ${shortYear}`,
        `19 Mar ${shortYear}`,
        `20 Mar ${shortYear}`,
        `21 Mar ${shortYear}`,
        `22 Mar ${shortYear}`,
        `23 Mar ${shortYear}`,
      ],
    },
    month: {
      current: [
        `May ${currentYear}`,
        `Jun ${currentYear}`,
        `Jul ${currentYear}`,
        `Aug ${currentYear}`,
        `Sep ${currentYear}`,
        `Oct ${currentYear}`,
      ],
      previous: [
        `May ${previousYear}`,
        `Jun ${previousYear}`,
        `Jul ${previousYear}`,
        `Aug ${previousYear}`,
        `Sep ${previousYear}`,
        `Oct ${previousYear}`,
      ],
    },
    week: {
      current: [
        `18 Aug ${currentYear} - 24 Aug ${currentYear}`,
        `25 Aug ${currentYear} - 31 Aug ${currentYear}`,
        `1 Sep ${currentYear} - 7 Sep ${currentYear}`,
        `8 Sep ${currentYear} - 14 Sep ${currentYear}`,
        `15 Sep ${currentYear} - 21 Sep ${currentYear}`,
        `22 Sep ${currentYear} - 28 Sep ${currentYear}`,
      ],
      previous: [
        `18 Aug ${previousYear} - 24 Aug ${previousYear}`,
        `25 Aug ${previousYear} - 31 Aug ${previousYear}`,
        `1 Sep ${previousYear} - 7 Sep ${previousYear}`,
        `8 Sep ${previousYear} - 14 Sep ${previousYear}`,
        `15 Sep ${previousYear} - 21 Sep ${previousYear}`,
        `22 Sep ${previousYear} - 28 Sep ${previousYear}`,
      ],
    },
  }
}

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/practice_performance_consultations',
    function (this: MirageServer, schema, request) {
      const period_type = getQueryParamValue(
        request?.queryParams?.period_type,
      ) as ReportSummaryTimePeriod['type']
      const periodTypeLowerCase = period_type.toLowerCase() as Lowercase<
        typeof period_type
      >

      const summary = server.create('summaryConsultation')
      const periodValues = getMockPeriodValues()

      const mockedMonthValues = {
        current: [25000, 24000, 28000, 27000, 26000, 23880],
        previous: [17000, 16000, 20000, 22000, 19000, 28100],
      }

      summary.attrs.current_series.forEach(
        (
          item: SummaryConsultationDataBE['current_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase].current[index]
          item.value = mockedMonthValues.current[index]
        },
      )

      summary.attrs.previous_series.forEach(
        (
          item: SummaryConsultationDataBE['previous_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase].previous[index]
          item.value = mockedMonthValues.previous[index]
        },
      )

      summary.attrs.current_total.value =
        summary.attrs.current_series[NR_OF_SERIES_ITEMS - 1].value

      summary.attrs.previous_total.value =
        summary.attrs.previous_series[NR_OF_SERIES_ITEMS - 1].value

      summary.attrs.totals_delta =
        summary.attrs.previous_total.value === 0
          ? null
          : Number(
              (
                ((summary.attrs.current_total.value -
                  summary.attrs.previous_total.value) /
                  summary.attrs.previous_total.value) *
                100
              ).toFixed(1),
            )

      return summary
    },
    { timing: 900 },
  )
}

export function seeds(server: MockServer) {
  server.create('summaryConsultation')
}
